import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const IPhoneStartetNichtPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'iPhone startet nicht mehr – Ursachen & Reparatur | PhoneMedic',
      description: 'iPhone startet nicht mehr? ✓ Ursachen & Sofortmaßnahmen ✓ Professionelle Reparatur ✓ 12 Monate Garantie ✓ Express 24h ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['iPhone startet nicht mehr', 'iPhone schaltet sich nicht ein', 'iPhone kein Lebenszeichen', 'iPhone bootet nicht', 'iPhone schwarzer Bildschirm startet nicht'],
      canonicalUrl: 'https://www.phone-medic.de/iphone-startet-nicht',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'iPhone startet nicht mehr – Diagnose & Reparatur',
        description: 'Professionelle Diagnose und Reparatur wenn das iPhone nicht mehr startet. Akku, Software, Wasserschaden oder Hardware – wir finden die Ursache.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: { '@type': 'Offer', price: '49.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Warum startet mein iPhone nicht mehr?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die häufigsten Ursachen: komplett entleerter Akku, defekter Akku, Software-Absturz (DFU-Modus hilft), Wasserschaden, defekter Ladeport oder ein Hardwareschaden am Logicboard.' },
          },
          {
            '@type': 'Question',
            name: 'Was soll ich zuerst versuchen wenn das iPhone nicht startet?',
            acceptedAnswer: { '@type': 'Answer', text: '1. Mindestens 30 Minuten laden. 2. Erzwungener Neustart: Volume Up drücken, Volume Down drücken, dann Side Button halten bis Apple-Logo erscheint. 3. DFU-Modus versuchen. Hilft keins davon, ist professionelle Reparatur nötig.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet die Reparatur wenn das iPhone nicht startet?',
            acceptedAnswer: { '@type': 'Answer', text: 'Das hängt von der Ursache ab. Ein Akkutausch beginnt ab 49€. Softwareprobleme können oft günstiger behoben werden. Bei Hardwareschäden wird zunächst eine Diagnose durchgeführt.' },
          },
          {
            '@type': 'Question',
            name: 'Mein iPhone zeigt nur ein leeres Akkusymbol – was tun?',
            acceptedAnswer: { '@type': 'Answer', text: 'Das leere Akkusymbol bedeutet der Akku ist tiefentladen. Lade das iPhone mindestens 30 Minuten mit dem Original-Ladekabel. Erscheint danach kein Apfel-Logo, ist der Akku möglicherweise defekt.' },
          },
          {
            '@type': 'Question',
            name: 'iPhone nach Sturz nicht mehr startbar – was ist kaputt?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nach einem Sturz können Display, Akku oder interne Verbindungen beschädigt sein. Manchmal löst sich nur ein Stecker, was eine schnelle und günstige Reparatur ermöglicht. Professionelle Diagnose zeigt die genaue Ursache.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#iphone-startet-nicht-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'iphone-startet-nicht-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#iphone-startet-nicht-schema')?.remove() }
  }, [])

  const causes = [
    { title: 'Entleerter oder defekter Akku', desc: 'Der häufigste Grund. Ein tiefentladener Akku zeigt kein Lebenszeichen. Ein defekter Akku kann das iPhone auch bei angezeigter Ladung nicht mehr starten lassen.' },
    { title: 'Software-Absturz', desc: 'iOS kann in seltenen Fällen so abstürzen, dass ein normaler Neustart nicht mehr funktioniert. Der DFU-Modus (Device Firmware Update) kann das Problem oft beheben.' },
    { title: 'Defekter Ladeport', desc: 'Wenn das iPhone nicht lädt, kann es auch nicht starten. Ein beschädigter Lightning- oder USB-C-Port verhindert die Stromzufuhr zum Akku.' },
    { title: 'Wasserschaden', desc: 'Feuchtigkeit kann Kurzschlüsse auf dem Logicboard verursachen. Auch bei IP67/IP68 zertifizierten iPhones kann Wasser eindringen, besonders bei älteren Geräten.' },
    { title: 'Sturz / Hardwareschaden', desc: 'Nach einem Sturz können sich interne Stecker lösen oder Chips beschädigt werden. Manchmal ist es nur ein gelöster Displaystecker – eine schnelle Reparatur.' },
    { title: 'Logicboard-Defekt', desc: 'In seltenen Fällen liegt der Defekt direkt auf der Hauptplatine. Hier hilft Chip-Level Diagnose unter dem Mikroskop.' },
  ]

  const steps = [
    { step: '1', title: 'Laden', desc: 'Schließe das iPhone ans Ladekabel an und warte mindestens 30 Minuten. Verwende wenn möglich ein anderes Kabel oder Netzteil.' },
    { step: '2', title: 'Erzwungener Neustart', desc: 'Lautstärke + drücken, Lautstärke - drücken, dann Einschalttaste halten bis das Apple-Logo erscheint (ca. 10 Sek.).' },
    { step: '3', title: 'DFU-Modus', desc: 'Wenn nichts hilft: iPhone per iTunes/Finder in den DFU-Modus versetzen und wiederherstellen. Achtung: alle Daten gehen verloren.' },
    { step: '4', title: 'Professionelle Diagnose', desc: 'Hilft keiner der obigen Schritte, liegt ein Hardwaredefekt vor. Wir diagnostizieren die genaue Ursache und beheben sie.' },
  ]

  const faqs = [
    { q: 'Warum startet mein iPhone nicht mehr?', a: 'Häufigste Ursachen: entleerter/defekter Akku, Software-Absturz, defekter Ladeport, Wasserschaden oder Sturz.' },
    { q: 'Was soll ich zuerst versuchen?', a: '1. 30 Minuten laden. 2. Erzwungener Neustart (Vol+, Vol-, Side-Button halten). 3. DFU-Modus. Hilft keins davon → professionelle Reparatur.' },
    { q: 'Was kostet die Reparatur?', a: 'Akkutausch ab 49€. Ladeport ab 59€. Bei Hardwareschäden zuerst Diagnose. Du erhältst immer einen Kostenvoranschlag bevor wir reparieren.' },
    { q: 'iPhone zeigt nur leeres Akkusymbol – was tun?', a: 'Mindestens 30 Minuten mit Original-Kabel laden. Erscheint danach kein Apple-Logo, ist der Akku möglicherweise defekt und muss getauscht werden.' },
    { q: 'iPhone nach Sturz nicht startbar – was ist kaputt?', a: 'Kann Display, Akku oder ein interner Stecker sein. Manchmal nur ein gelöster Stecker – schnelle und günstige Reparatur. Diagnose zeigt die genaue Ursache.' },
    { q: 'Gehen meine Daten verloren?', a: 'Bei Hardware-Reparaturen (Akku, Ladeport) bleiben Daten erhalten. Bei einer Software-Wiederherstellung über DFU gehen Daten verloren – Backup empfohlen.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Diagnose · Akku · Software · Hardware
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              iPhone startet nicht mehr –<br />
              <span className="text-indigo-400">Ursachen & Reparatur</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Schwarzer Bildschirm, kein Lebenszeichen, Apple-Logo hängt? Wir finden die Ursache und reparieren dein iPhone – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Reparatur anfragen
              </a>
              <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 49 €', label: 'Reparatur' },
              { number: 'Express', label: '24h Service' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Warum startet das iPhone nicht mehr?</h2>
            <p className="text-gray-500">Die häufigsten Ursachen – von einfach bis komplex</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {causes.map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Das kannst du selbst versuchen</h2>
            <p className="text-gray-500">Schritt für Schritt – bevor du einschickst</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((item, i) => (
              <div key={i} className="text-center flex flex-col items-center">
                <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">{item.step}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-xl p-5 text-center">
            <p className="text-indigo-800 text-sm">
              Helfen diese Schritte nicht? Dann liegt sehr wahrscheinlich ein Hardwaredefekt vor – und der lässt sich in den meisten Fällen professionell reparieren.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere iPhone Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'iPhone Akku tauschen', path: '/iphone-akku-tauschen' },
              { label: 'iPhone Display Reparatur', path: '/iphone-display-reparatur' },
              { label: 'iPhone Neustart-Schleife', path: '/iphone-neustart-schleife' },
              { label: 'Alle iPhone Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">iPhone startet nicht? Wir finden die Ursache.</h2>
          <p className="text-gray-400 mb-8">Reparatur ab 49 € · Kostenvoranschlag · 12 Monate Garantie</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default IPhoneStartetNichtPage
